import type { SVGProps } from 'react';

/**
 * A small, consistent icon set drawn inline so the app ships no icon font and
 * no runtime dependency. Every glyph is a 24×24 stroked path on `currentColor`,
 * sized with `size-*` utilities at the call site.
 *
 * Icons are decorative by default (`aria-hidden`); pass a `title` when an icon
 * is the only label for a control.
 */
export type IconProps = SVGProps<SVGSVGElement> & { title?: string };

function Icon({ title, children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...props}
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

export const SearchIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Icon>
);

export const BriefcaseIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.5" y="7" width="19" height="13" rx="2.5" />
    <path d="M8.5 7V5.5A2 2 0 0 1 10.5 3.5h3a2 2 0 0 1 2 2V7M2.5 12.5h19" />
  </Icon>
);

export const ShieldCheckIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 2.75 4.75 5.5v6c0 4.6 3 8.2 7.25 9.75 4.25-1.55 7.25-5.15 7.25-9.75v-6z" />
    <path d="m9 12 2.25 2.25L15.5 10" />
  </Icon>
);

export const ShoppingBagIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 7.5h15l-1.2 12a2 2 0 0 1-2 1.8H7.7a2 2 0 0 1-2-1.8z" />
    <path d="M8.75 10.5v-4a3.25 3.25 0 0 1 6.5 0v4" />
  </Icon>
);

export const BabyIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="8" r="4.25" />
    <path d="M10.25 7.75h.01M13.75 7.75h.01M10.5 10.25c.9.7 2.1.7 3 0" />
    <path d="M5.5 21v-1.5A4.5 4.5 0 0 1 10 15h4a4.5 4.5 0 0 1 4.5 4.5V21" />
  </Icon>
);

export const BookIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 4.75A1.75 1.75 0 0 1 5.75 3H11a2 2 0 0 1 2 2v14a1.5 1.5 0 0 0-1.5-1.5H5.75A1.75 1.75 0 0 1 4 15.75z" />
    <path d="M20 4.75A1.75 1.75 0 0 0 18.25 3H15a2 2 0 0 0-2 2v14a1.5 1.5 0 0 1 1.5-1.5h3.75A1.75 1.75 0 0 0 20 15.75z" />
  </Icon>
);

export const PawIcon = (p: IconProps) => (
  <Icon {...p}>
    <ellipse cx="7" cy="8.5" rx="1.9" ry="2.4" />
    <ellipse cx="12" cy="6.5" rx="1.9" ry="2.4" />
    <ellipse cx="17" cy="8.5" rx="1.9" ry="2.4" />
    <path d="M12 12c2.6 0 5 2.1 5 4.6 0 2-1.5 3.2-3.3 2.8-1.1-.25-2.3-.25-3.4 0C8.5 19.8 7 18.6 7 16.6 7 14.1 9.4 12 12 12" />
  </Icon>
);

export const LeafIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 20c0-8 5-13.5 16-13.5C20 15 15 20.5 6.5 19.5" />
    <path d="M4 20c2.5-4 5.5-6.5 9.5-8.5" />
  </Icon>
);

export const LaptopIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="5" width="16" height="11" rx="2" />
    <path d="M2 19.5h20" />
  </Icon>
);

export const HandshakeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m11 7.5-2 2a2 2 0 0 0 0 2.8 2 2 0 0 0 2.8 0l1.4-1.4 3.3 3.3a1.9 1.9 0 0 1-2.7 2.7l-.6-.6" />
    <path d="M13 17.3a1.9 1.9 0 1 1-2.7 2.7l-3.6-3.6" />
    <path d="M2.5 8.5 6 5.5l5 1.5 4-1.5 3.5 3M21.5 8.5 19 6M2.5 8.5 5 6" />
  </Icon>
);

export const SparkleIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M18.5 15.5 19 17l1.5.5-1.5.5-.5 1.5-.5-1.5L16.5 17l1.5-.5z" />
  </Icon>
);

export const CheckIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </Icon>
);

export const BadgeCheckIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m12 2.5 2.4 1.9 3-.3 1 2.9 2.6 1.6-1 2.9 1 2.9-2.6 1.6-1 2.9-3-.3L12 21.5l-2.4-1.9-3 .3-1-2.9L3 15.4l1-2.9-1-2.9 2.6-1.6 1-2.9 3 .3z" />
    <path d="m9 12 2.25 2.25L15.5 10" />
  </Icon>
);

export const ArrowRightIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 12h15M13.5 6l6 6-6 6" />
  </Icon>
);

export const MapPinIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11" />
    <circle cx="12" cy="10" r="2.6" />
  </Icon>
);

export const ClockIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.75" />
    <path d="M12 7v5.2l3.2 2" />
  </Icon>
);

export const WalletIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 7.5A2 2 0 0 1 5.5 5.5h11a2 2 0 0 1 2 2v1" />
    <rect x="3.5" y="7.5" width="17" height="11.5" rx="2.5" />
    <path d="M16.5 13.25h.01" />
  </Icon>
);

export const BellIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.5 9.5a5.5 5.5 0 0 1 11 0c0 4 1.5 5.5 1.5 5.5H5s1.5-1.5 1.5-5.5" />
    <path d="M10.25 18.5a2 2 0 0 0 3.5 0" />
  </Icon>
);

export const EyeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2.5 12S6 5.75 12 5.75 21.5 12 21.5 12 18 18.25 12 18.25 2.5 12 2.5 12" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

export const LockIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7.75a4 4 0 0 1 8 0V10.5" />
  </Icon>
);

export const ChatIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20.5 11.5c0 4-3.8 7.25-8.5 7.25a9.7 9.7 0 0 1-2.6-.35L4.5 20l1.2-3.4A6.9 6.9 0 0 1 3.5 11.5c0-4 3.8-7.25 8.5-7.25s8.5 3.25 8.5 7.25" />
  </Icon>
);

export const UsersIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9.5" cy="8" r="3.5" />
    <path d="M3.5 19.5v-.75A4.75 4.75 0 0 1 8.25 14h2.5a4.75 4.75 0 0 1 4.75 4.75v.75" />
    <path d="M16.5 5.1a3.5 3.5 0 0 1 0 6.3M17.5 14.2a4.75 4.75 0 0 1 3 4.4v.9" />
  </Icon>
);

export const MenuIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
);

export const CloseIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Icon>
);

export const ChevronDownIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 9.5 6 6 6-6" />
  </Icon>
);

export const StarIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m12 3.75 2.6 5.27 5.82.85-4.21 4.1 1 5.8L12 17.03l-5.21 2.74 1-5.8-4.21-4.1 5.82-.85z" />
  </Icon>
);

export const CalendarIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="5" width="17" height="15" rx="3" />
    <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
  </Icon>
);

export const CreditCardIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.75" y="5.5" width="18.5" height="13" rx="3" />
    <path d="M2.75 10h18.5M6.5 14.75h3" />
  </Icon>
);

export const UserCheckIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10" cy="8" r="3.75" />
    <path d="M3.5 19.5v-.9A4.6 4.6 0 0 1 8.1 14h3.8a4.6 4.6 0 0 1 3.1 1.2" />
    <path d="m15.75 17.75 1.9 1.9 3.6-3.9" />
  </Icon>
);

export const PhoneIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8.1 4.5 9.6 8l-1.8 1.7a11.5 11.5 0 0 0 4.9 4.9l1.7-1.8 3.5 1.5v3a1.9 1.9 0 0 1-2.1 1.9C9.9 18.6 5.4 14.1 4.7 7.2A1.9 1.9 0 0 1 6.6 4.5z" />
  </Icon>
);

export const MailIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="5.5" width="18" height="13" rx="3" />
    <path d="m4 7.5 8 5.5 8-5.5" />
  </Icon>
);

export const TrendingUpIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m3.5 16.5 5-5.5 3.5 3.5 5.5-6.5" />
    <path d="M14.5 8h3.5v3.5" />
  </Icon>
);

export const BoltIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M13.5 3 5 13.5h5.5L10 21l8.5-10.5H13z" />
  </Icon>
);
